import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Alert,
  Skeleton,
  Stack,
  Paper,
  useTheme,
  Container,
  Tab,
  Tabs,
  ListItemButton,
  Divider,
  Grid,
  ButtonBase,
} from "@mui/material";
import {
  Event,
  OpenInNew,
  TrendingUp,
  Pets,
  DirectionsRun,
  Groups,
  Group,
  Settings,
  ChevronRight,
} from "@mui/icons-material";
import axios from "axios";
import Cookies from "universal-cookie";
import { useLocation, useNavigate } from "react-router-dom";
import { isAdmin, isClubAdmin } from "../../utils/jwt";
import { featureFlags } from "../../utils/featureFlags";
import { useHasManagedClubs } from "../AgilityClubs/useHasManagedClubs";
import CreateClubPrompt from "../AgilityClubs/CreateClubPrompt";
import BankTransferPaymentDialog, {
  BankTransferPaymentDetails,
} from "../AgilityClubs/BankTransferPaymentDialog";
import {
  buildCompactInvoiceReference,
  sumInvoiceAmounts,
} from "../AgilityClubs/invoicePaymentUtils";
import HomeInvoicesSection from "./HomeInvoicesSection";
import HomeSectionHeader from "./HomeSectionHeader";
import MailingListSection from "./MailingListSection";
import {
  collectMembershipInvoices,
  type MembershipInvoiceRow,
} from "../ClubMemberships/clubSummary";

export type ShowEntry = {
  id: number;
  schedule_url?: string;
  ring_plan_url?: string;
  show_name: string;
  show_date: string;
  show_entries: Entry[];
};

export type Entry = {
  id: number;
  class_name: string;
  class_id: number;
  dog_name: string;
  eliminated: false;
  faults: string[];
  handler_name: string;
  nfc_run: false;
  time: number;
  withdrawn: boolean;
};

type EntryTab = "upcoming" | "unpaid" | "past";

const TAB_ORDER: EntryTab[] = ["upcoming", "unpaid", "past"];

type EntryStatus = {
  label: "Unpaid" | "This week" | "Upcoming" | "Past";
  color: "warning" | "default" | "success" | "info";
};

const startOfDay = (date: Date): Date => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

/** Parse API show dates as local calendar days (avoids UTC date-only shifting). */
const parseShowDate = (showDateString: string): Date => {
  const isoDate = /^(\d{4})-(\d{2})-(\d{2})/.exec(showDateString);
  if (isoDate) {
    return startOfDay(
      new Date(
        Number(isoDate[1]),
        Number(isoDate[2]) - 1,
        Number(isoDate[3]),
      ),
    );
  }
  return startOfDay(new Date(showDateString));
};

const isUnpaid = (entry: ShowEntry): boolean =>
  (entry.show_entries?.length ?? 0) === 0;

const isPastShow = (showDateString: string): boolean => {
  const today = startOfDay(new Date());
  const showDate = parseShowDate(showDateString);
  return showDate < today;
};

const isUpcomingShow = (showDateString: string): boolean =>
  !isPastShow(showDateString);

const getEntryStatus = (entry: ShowEntry): EntryStatus => {
  if (isUnpaid(entry)) {
    return { label: "Unpaid", color: "warning" };
  }

  if (isPastShow(entry.show_date)) {
    return { label: "Past", color: "default" };
  }

  const today = startOfDay(new Date());
  const showDate = parseShowDate(entry.show_date);
  const oneWeekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);

  if (showDate <= oneWeekFromNow) {
    return { label: "This week", color: "info" };
  }

  return { label: "Upcoming", color: "success" };
};

const sortByShowDate = (
  items: ShowEntry[],
  direction: "asc" | "desc",
): ShowEntry[] =>
  [...items].sort((a, b) => {
    const diff =
      new Date(a.show_date).getTime() - new Date(b.show_date).getTime();
    return direction === "asc" ? diff : -diff;
  });

const formatShortDate = (dateString: string): string =>
  parseShowDate(dateString).toLocaleDateString("en-GB", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });

const formatLongDate = (dateString: string): string =>
  parseShowDate(dateString).toLocaleDateString("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

const filterEntriesForTab = (
  tab: EntryTab,
  allEntries: ShowEntry[] | null | undefined,
): ShowEntry[] => {
  const list = Array.isArray(allEntries) ? allEntries : [];
  switch (tab) {
    case "upcoming":
      return sortByShowDate(
        list.filter((entry) => isUpcomingShow(entry.show_date)),
        "asc",
      );
    case "unpaid":
      return sortByShowDate(
        list.filter(
          (entry) => isUnpaid(entry) && isUpcomingShow(entry.show_date),
        ),
        "asc",
      );
    case "past":
      return sortByShowDate(
        list.filter(
          (entry) => isPastShow(entry.show_date) && !isUnpaid(entry),
        ),
        "desc",
      );
    default:
      return [];
  }
};

const pickDefaultTab = (
  upcoming: ShowEntry[],
  unpaid: ShowEntry[],
): EntryTab => {
  if (upcoming.length > 0) return "upcoming";
  if (unpaid.length > 0) return "unpaid";
  return "past";
};

const EMPTY_TAB_MESSAGES: Record<EntryTab, { title: string; body: string }> = {
  upcoming: {
    title: "No upcoming shows",
    body: "Enter a show via Show Diary when you're ready to compete.",
  },
  unpaid: {
    title: "No unpaid entries",
    body: "Shows awaiting payment will appear here.",
  },
  past: {
    title: "No past shows",
    body: "Your previous show entries will appear here.",
  },
};

const Entries = (): JSX.Element => {
  const [entries, setEntries] = useState<ShowEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userIsAdmin, setUserIsAdmin] = useState(false);
  const [userIsClubAdmin, setUserIsClubAdmin] = useState(false);
  const [activeTab, setActiveTab] = useState<EntryTab>("upcoming");
  const [tabInitialized, setTabInitialized] = useState(false);
  const [clubInvoices, setClubInvoices] = useState<MembershipInvoiceRow[]>([]);
  const [paymentDetails, setPaymentDetails] =
    useState<BankTransferPaymentDetails | null>(null);
  const location = useLocation();
  const { hasManagedClubs, loading: clubsLoading } = useHasManagedClubs(
    featureFlags.clubAdministration,
    location.pathname,
  );
  const showClubAdminNav =
    featureFlags.clubAdministration && (userIsClubAdmin || hasManagedClubs);
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    getEntries();
    setUserIsAdmin(isAdmin());
    setUserIsClubAdmin(isClubAdmin());
    if (featureFlags.clubMemberships) {
      getClubInvoices();
    }
  }, []);

  const getClubInvoices = async () => {
    try {
      const token = new Cookies().get("token");
      if (!token) {
        setClubInvoices([]);
        return;
      }

      const response = await axios.get(
        "https://api.easyagility.co.uk/my-memberships",
        { headers: { Authorization: "Bearer " + token } },
      );

      setClubInvoices(collectMembershipInvoices(response.data));
    } catch (err) {
      console.error("Error fetching club invoices:", err);
      setClubInvoices([]);
    }
  };

  const getEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = new Cookies().get("token");

      const response = await axios.get(
        "https://api.easyagility.co.uk/show-entries",
        { headers: { Authorization: "Bearer " + token } },
      );

      setEntries(Array.isArray(response.data) ? response.data : []);
      setTabInitialized(false);
    } catch (err) {
      setError("Failed to load show entries. Please try again later.");
      console.error("Error fetching entries:", err);
    } finally {
      setLoading(false);
    }
  };

  const { upcomingEntries, unpaidEntries, pastEntries } = useMemo(
    () => ({
      upcomingEntries: filterEntriesForTab("upcoming", entries),
      unpaidEntries: filterEntriesForTab("unpaid", entries),
      pastEntries: filterEntriesForTab("past", entries),
    }),
    [entries],
  );

  useEffect(() => {
    if (!tabInitialized && !loading) {
      setActiveTab(pickDefaultTab(upcomingEntries, unpaidEntries));
      setTabInitialized(true);
    }
  }, [loading, tabInitialized, upcomingEntries, unpaidEntries]);

  const visibleEntries = useMemo(
    () => filterEntriesForTab(activeTab, entries),
    [activeTab, entries],
  );

  const activeTabIndex = TAB_ORDER.indexOf(activeTab);

  const handleShowClick = (entry: ShowEntry) => {
    navigate(`/shows/${entry.id}`);
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleInvoicePayClick = (invoices: MembershipInvoiceRow[]) => {
    if (invoices.length === 0) return;
    setPaymentDetails({
      paymentReference: buildCompactInvoiceReference(
        invoices.map((inv) => inv.id),
      ),
      amount: sumInvoiceAmounts(invoices),
    });
  };

  const handleClosePaymentDialog = () => {
    setPaymentDetails(null);
  };

  const secondaryLinks = [
    {
      label: "View Results",
      icon: TrendingUp,
      color: theme.palette.info.main,
      onClick: () =>
        handleExternalLink(
          "https://results.easyagility.co.uk/previous-shows",
        ),
      external: true,
    },
    {
      label: "My Dogs",
      icon: Pets,
      color: theme.palette.primary.main,
      onClick: () => navigate("/dogs"),
      external: false,
    },
    {
      label: "My Handlers",
      icon: DirectionsRun,
      color: "#0097A7",
      onClick: () => navigate("/handlers"),
      external: false,
    },
    ...(featureFlags.clubMemberships
      ? [
          {
            label: "Club Memberships",
            icon: Groups,
            color: "#6a1b9a",
            onClick: () => navigate("/club-memberships"),
            external: false,
          },
        ]
      : []),
    ...(showClubAdminNav
      ? [
          {
            label: "Club Administration",
            icon: Group,
            color: "#00695C",
            onClick: () => navigate("/agility-clubs"),
            external: false,
          },
        ]
      : []),
    ...(userIsAdmin
      ? [
          {
            label: "Show Management",
            icon: Settings,
            color: theme.palette.warning.dark,
            onClick: () => navigate("/show-management"),
            external: false,
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="text" width="40%" height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={88} sx={{ mb: 3, borderRadius: 2 }} />
        <Stack spacing={1}>
          {[...Array(5)].map((_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={72}
              sx={{ borderRadius: 2 }}
            />
          ))}
        </Stack>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          alignItems: { xs: "stretch", sm: "flex-start" },
          justifyContent: "space-between",
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 700,
              color: theme.palette.primary.main,
              mb: 0.5,
              fontSize: { xs: "2rem", md: "2.5rem" },
            }}
          >
            Home
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: { xs: "1rem", md: "1.05rem" },
            }}
          >
            Show entries, club invoices, and your account shortcuts
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<Event />}
          onClick={() => handleExternalLink("https://entries.easyagility.co.uk")}
          sx={{
            alignSelf: { xs: "stretch", sm: "flex-start" },
            py: 1.25,
            px: 3,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "1rem",
            fontWeight: 600,
            backgroundColor: theme.palette.success.main,
            whiteSpace: "nowrap",
            "&:hover": { backgroundColor: theme.palette.success.dark },
          }}
        >
          Show Diary &amp; Enter
        </Button>
      </Box>

      <MailingListSection authenticated />

      <Paper
        variant="outlined"
        sx={{
          mb: 3,
          p: { xs: 1.25, sm: 1.5 },
          borderRadius: 2,
          borderColor: `${theme.palette.primary.main}20`,
          bgcolor: `${theme.palette.primary.main}06`,
        }}
      >
        <Grid container spacing={1}>
          {secondaryLinks.map((link) => {
            const Icon = link.icon;
            return (
              <Grid item xs={6} sm={4} lg={2} key={link.label} sx={{ minWidth: 0 }}>
                <ButtonBase
                  onClick={link.onClick}
                  sx={{
                    display: "block",
                    width: "100%",
                    borderRadius: 1.5,
                    textAlign: "left",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.25,
                      px: 1.25,
                      py: 1,
                      borderRadius: 1.5,
                      transition: "background-color 0.15s ease",
                      "&:hover": {
                        bgcolor: "background.paper",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 32,
                        height: 32,
                        borderRadius: 1.25,
                        bgcolor: link.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        "& .MuiSvgIcon-root": {
                          color: "white",
                          fontSize: 18,
                        },
                      }}
                    >
                      <Icon />
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontWeight: 600,
                        color: "text.primary",
                        lineHeight: 1.3,
                      }}
                    >
                      {link.label}
                    </Typography>
                    {link.external ? (
                      <OpenInNew
                        sx={{ fontSize: 14, color: "text.disabled", flexShrink: 0 }}
                      />
                    ) : (
                      <ChevronRight
                        sx={{ fontSize: 18, color: "text.disabled", flexShrink: 0 }}
                      />
                    )}
                  </Box>
                </ButtonBase>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      {featureFlags.clubMemberships && clubInvoices.length > 0 && (
        <HomeInvoicesSection
          invoices={clubInvoices}
          onPayClick={handleInvoicePayClick}
        />
      )}

      {featureFlags.clubAdministration && !clubsLoading && !hasManagedClubs && (
        <CreateClubPrompt />
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={getEntries}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {entries.length === 0 && !error ? (
        <Box sx={{ mb: 3 }}>
          <HomeSectionHeader
            icon={<Event color="primary" sx={{ fontSize: 22 }} />}
            title="Show entries"
            subtitle="No shows registered yet"
          />
          <Card
            sx={{
              textAlign: "center",
              py: 8,
              px: 4,
              backgroundColor: theme.palette.background.paper,
              borderRadius: 3,
              boxShadow: theme.shadows[2],
            }}
          >
            <Event
              sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }}
            />
            <Typography
              variant="h6"
              sx={{
                color: theme.palette.text.secondary,
                mb: 1,
                fontWeight: 500,
              }}
            >
              No Show Entries Yet
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.text.disabled,
                mb: 3,
              }}
            >
              You haven&apos;t entered any shows yet. Use Show Diary to get
              started.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Event />}
              onClick={() =>
                handleExternalLink("https://entries.easyagility.co.uk")
              }
              sx={{
                borderRadius: 2,
                px: 3,
                py: 1.5,
              }}
            >
              Enter Your First Show
            </Button>
          </Card>
        </Box>
      ) : (
        <Box sx={{ mb: 3 }}>
          <HomeSectionHeader
            icon={<Event color="primary" sx={{ fontSize: 22 }} />}
            title="Show entries"
            subtitle={
              unpaidEntries.length > 0
                ? `${entries.length} show${entries.length === 1 ? "" : "s"} · ${unpaidEntries.length} awaiting payment`
                : `${entries.length} registered show${entries.length === 1 ? "" : "s"}`
            }
          />
          <Tabs
            value={activeTabIndex === -1 ? 0 : activeTabIndex}
            onChange={(_, index: number) => setActiveTab(TAB_ORDER[index])}
            sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}
            aria-label="Show entry filters"
          >
            <Tab
              label={`Upcoming (${upcomingEntries.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              label={`Unpaid (${unpaidEntries.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
            <Tab
              label={`Past (${pastEntries.length})`}
              sx={{ textTransform: "none", fontWeight: 600 }}
            />
          </Tabs>

          {visibleEntries.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                textAlign: "center",
                py: 6,
                px: 3,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                {EMPTY_TAB_MESSAGES[activeTab].title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {EMPTY_TAB_MESSAGES[activeTab].body}
              </Typography>
            </Paper>
          ) : (
            <Paper
              key={activeTab}
              variant="outlined"
              sx={{ borderRadius: 2, overflow: "hidden" }}
            >
              {visibleEntries.map((entry, index) => {
                const status = getEntryStatus(entry);
                const classesEntered = entry.show_entries?.length ?? 0;

                return (
                  <React.Fragment key={entry.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton
                      onClick={() => handleShowClick(entry)}
                      sx={{
                        py: { xs: 2, sm: 1.75 },
                        px: { xs: 2, sm: 2.5 },
                        alignItems: { xs: "flex-start", sm: "center" },
                        flexDirection: { xs: "column", sm: "row" },
                        gap: { xs: 1, sm: 2 },
                      }}
                      aria-label={`View details for ${entry.show_name} on ${formatLongDate(entry.show_date)}`}
                    >
                      <Box sx={{ flex: 1, minWidth: 0, width: "100%" }}>
                        <Typography
                          variant="subtitle1"
                          component="h2"
                          sx={{ fontWeight: 600, lineHeight: 1.3 }}
                        >
                          {entry.show_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatShortDate(entry.show_date)}
                        </Typography>
                      </Box>
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        flexWrap="wrap"
                        useFlexGap
                        sx={{ width: { xs: "100%", sm: "auto" } }}
                      >
                        {classesEntered > 0 && (
                          <Typography variant="body2" color="text.secondary">
                            {classesEntered}{" "}
                            {classesEntered === 1 ? "class" : "classes"}
                          </Typography>
                        )}
                        <Chip
                          label={status.label}
                          color={status.color}
                          size="small"
                          sx={{ fontWeight: 500 }}
                        />
                      </Stack>
                    </ListItemButton>
                  </React.Fragment>
                );
              })}
            </Paper>
          )}
        </Box>
      )}

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mt: 4, textAlign: "center" }}
      >
        Need help?{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          matt@easyagility.co.uk
        </Box>
      </Typography>

      <BankTransferPaymentDialog
        open={paymentDetails != null}
        details={paymentDetails}
        onClose={handleClosePaymentDialog}
      />
    </Container>
  );
};

export default Entries;
